import { supabase } from '@/services/supabase/supabase';
import { toast } from '@/components/ui/use-toast';
import { createWritingSession } from '@/services/progress-tracker/api';

// Constants for local storage keys and timeouts
export const TIMER_SESSION_KEY = 'cognito_timer_session';
export const TIMER_VISIBILITY_KEY = 'cognito_timer_visibility';
export const MAX_INACTIVE_SECONDS = 43200; // 12 hours max inactivity before session expires

// Format time display
export const formatTime = (totalSeconds: number) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [
    hours.toString().padStart(2, '0'),
    minutes.toString().padStart(2, '0'),
    seconds.toString().padStart(2, '0')
  ].join(':');
};

// Save session state to localStorage
export const saveSessionToLocalStorage = (session: any) => {
  try {
    localStorage.setItem(TIMER_SESSION_KEY, JSON.stringify(session));
    return true;
  } catch (e) {
    console.error('Error saving session to localStorage:', e);
    return false;
  }
};

// Get session state from localStorage
export const getSessionFromLocalStorage = () => {
  try {
    const savedSession = localStorage.getItem(TIMER_SESSION_KEY);
    if (!savedSession) return null;
    return JSON.parse(savedSession);
  } catch (e) {
    console.error('Error getting session from localStorage:', e);
    return null;
  }
};

// Clear session state from localStorage
export const clearSessionFromLocalStorage = () => {
  try {
    localStorage.removeItem(TIMER_SESSION_KEY);
    return true;
  } catch (e) {
    console.error('Error clearing session from localStorage:', e);
    return false;
  }
};

// Save timer visibility state to localStorage
export const saveTimerVisibility = (isVisible: boolean) => {
  try {
    localStorage.setItem(TIMER_VISIBILITY_KEY, JSON.stringify(isVisible));
    return true;
  } catch (e) {
    console.error('Error saving timer visibility to localStorage:', e);
    return false;
  }
};

// Get timer visibility state from localStorage
export const getTimerVisibility = () => {
  try {
    const savedVisibility = localStorage.getItem(TIMER_VISIBILITY_KEY);
    return savedVisibility ? JSON.parse(savedVisibility) : true;
  } catch (e) {
    console.error('Error getting timer visibility from localStorage:', e);
    return true;
  }
};

// End session and save to database
export const endAndSaveSession = async (
  projectId: string | number,
  sessionSeconds: number,
  onComplete?: () => void
) => {
  console.log('Ending session at', sessionSeconds, 'seconds');

  // Capture the current session seconds
  const finalSessionSeconds = sessionSeconds;

  // Get the final session data
  try {
    const savedSession = getSessionFromLocalStorage();
    if (savedSession) {
      // Use the latest duration (either from state or localStorage)
      const finalDuration = Math.max(finalSessionSeconds, savedSession.duration || 0);

      // Only save if we have some duration
      if (finalDuration > 0) {
        console.log('Saving session to database:', {
          projectId,
          duration: finalDuration,
          startTime: savedSession.startTime,
          startTimeType: typeof savedSession.startTime,
          fullSession: savedSession
        });

        // Get the current story ID
        try {
          // Try to get the first story for the project
          const { data } = await supabase
            .from('stories')
            .select('id')
            .eq('project_id', projectId)
            .limit(1)
            .single();

          if (data) {
            const storyId = data.id;

            // Validate and create start date
            let startDate: Date;
            try {
              startDate = savedSession.startTime ? new Date(savedSession.startTime) : new Date();
              // Check if the date is valid
              if (isNaN(startDate.getTime())) {
                console.warn('Invalid start time in session, using current time');
                startDate = new Date();
              }
            } catch (dateError) {
              console.warn('Error parsing start time, using current time:', dateError);
              startDate = new Date();
            }

            // Save to database
            const result = await createWritingSession({
              storyId: storyId,
              duration: finalDuration,
              date: startDate,
              words_added: 0,
              words_deleted: 0,
              projectId: projectId
            });

            console.log('Session saved to database:', result);
            toast({
              description: 'Writing session saved!',
            });
          } else {
            toast({
              description: 'Could not save session: No story found.',
              variant: 'destructive'
            });
          }
        } catch (error) {
          console.error('Error finding story for project:', error);
          toast({
            description: 'Could not save session: No story found.',
            variant: 'destructive'
          });
        }
      } else {
        console.log('Not saving session with zero duration');
        toast({
          description: 'Session ended (no data saved for sessions with 0 duration).',
        });
      }

      // Clear the session data
      clearSessionFromLocalStorage();
    }
  } catch (e) {
    console.error('Error ending session:', e);
    toast({
      variant: 'destructive',
      description: 'Error saving session. Please try again.',
    });
  }

  // Call the completion callback if provided
  if (onComplete) {
    onComplete();
  }

  console.log('Session end complete');
  return true;
};
